/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography2 as Typography } from '@coursera/cds-core';
import { StarFilledIcon, StarHalfFilledIcon, StarIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/reviews-common';

const MAX_STARS = 5;

type Props = {
  rating: number;
  numReviews?: number;
  size?: React.ComponentProps<typeof StarFilledIcon>['size'];
  color?: React.ComponentProps<typeof StarFilledIcon>['color'];
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
    color: var(--cds-color-neutral-primary);
    line-height: 1;
  `,
  stars: css`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    line-height: 0;
    vertical-align: middle;
  `,
  starWrapper: css`
    display: inline-flex;
    align-items: center;
    line-height: 0;
  `,
  ratingNumber: css`
    font-weight: 700;
    color: var(--cds-color-neutral-primary);
  `,
  dotSeparator: css`
    margin: 0 var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary-weak);
  `,
  reviewCount: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

const RatingLine = ({ rating, numReviews, size = 'medium', color = 'interactive' }: Props) => {
  const roundedForStars = Math.round(rating * 2) / 2;
  const fullCount = Math.floor(roundedForStars);
  const hasHalf = roundedForStars % 1 === 0.5;
  const displayRating = rating.toFixed(1);

  const stars: React.ReactNode[] = [];
  for (let i = 0; i < fullCount; i++) {
    stars.push(
      <span key={`full-${i}`} css={styles.starWrapper}>
        <StarFilledIcon color={color} size={size} aria-hidden />
      </span>
    );
  }
  if (hasHalf) {
    stars.push(
      <span key="half" css={styles.starWrapper}>
        <StarHalfFilledIcon color={color} size={size} aria-hidden />
      </span>
    );
  }
  for (let i = stars.length; i < MAX_STARS; i++) {
    stars.push(
      <span key={`empty-${i}`} css={styles.starWrapper}>
        <StarIcon color={color} size={size} aria-hidden />
      </span>
    );
  }

  return (
    <div
      css={styles.container}
      data-testid="rating-line"
      aria-label={_t('Rated #{displayRating} out of 5 stars', { displayRating })}
    >
      <span css={styles.stars} aria-hidden>
        {stars}
      </span>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography component="span" variant="subtitleSmall" css={styles.ratingNumber}>
        {displayRating}
      </Typography>
      {numReviews && (
        <React.Fragment>
          <span css={styles.dotSeparator} aria-hidden>
            ·
          </span>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography component="span" variant="bodySecondary" css={styles.reviewCount}>
            <FormattedMessage message={_t('{numReviews} reviews')} numReviews={numReviews.toLocaleString()} />
          </Typography>
        </React.Fragment>
      )}
    </div>
  );
};

export default RatingLine;
